import * as React from "react";
import { useState } from "react";
import Typography from "@mui/joy/Typography";
import { Select, Option } from "@mui/joy";
import {
  Button,
  List,
  ListItem,
  Dialog,
  ListItemText,
  Divider,
  Box,
} from "@mui/material";
import { AddRounded, RemoveRounded } from "@mui-symbols-material/w300";
import { DashboardWidget, WidgetConfig, WidgetInstance } from "../DashboardWidgetConfig";
import { useTheme } from "@mui/joy";
import { BasicModalContent } from "@dreebit-registry/vsm-microservice-components";
import {useTranslation} from "react-i18next";

interface DashboardWidgetsModalProps {
  widgetTypes: DashboardWidget[];
  open: boolean;
  availableWidgets: DashboardWidget[];
  onClose: () => void;
  onSelect: (type: string, config?: WidgetConfig) => void;
  onRemoveInstance: (instanceId: string) => void;
  widgetIds: string[];
  instances: WidgetInstance[];
}

const DashboardWidgetsModal: React.FC<DashboardWidgetsModalProps> = ({
                                                                       widgetTypes,
                                                                       open,
                                                                       availableWidgets,
                                                                       onClose,
                                                                       onSelect,
                                                                       onRemoveInstance,
                                                                       widgetIds,
                                                                       instances,
                                                                     }) => {
  const {t} = useTranslation();
  const theme = useTheme();

  // Track selected config option for each configurable widget type
  const [selectedConfigs, setSelectedConfigs] = useState<Record<string, string>>({});

  // Get already used config values for a widget type
  const getUsedConfigValues = (widgetType: string): string[] => {
    const widget = widgetTypes.find(w => w.type === widgetType);
    const configKey = widget?.configKey ?? 'kpiType';
    return instances
      .filter(i => i.type === widgetType && i.config?.[configKey])
      .map(i => i.config![configKey] as string);
  };

  const handleConfigChange = (widgetType: string, value: string | null) => {
    if (value) {
      setSelectedConfigs(prev => ({ ...prev, [widgetType]: value }));
    }
  };

  return (
      <Dialog
          onClose={onClose}
          open={open}
          sx={{
            "& .MuiDialog-paper": {
              minWidth: 600,
              minHeight: 500,
              display: "flex",
            },
          }}
      >
        <BasicModalContent
            style={{
              padding: "16px",
              minWidth: "600px",
              minHeight: "550px",
              gap: "13px",
            }}
            onClose={onClose}
            title={
              <Box className={"flex-row"} sx={{ alignItems: "center", gap: "4px" }}>
                <Typography level={"titleSmall"}>{t("Choose a widget")}</Typography>
              </Box>
            }
        >
          <List sx={{ pt: 0, minWidth: "300px" }}>
            {widgetTypes.map((widget) => {
              const isAlreadyAdded = !widget.configurable && widgetIds.includes(widget.type);
              const allConfigOptions = widget.configOptions || [];
              const usedConfigValues = getUsedConfigValues(widget.type);
              const hasConfigOptions = widget.configurable && allConfigOptions.length > 0;

              // Default selection: first unused option, or first option if all are on dashboard
              const firstUnusedOption = allConfigOptions.find(opt => !usedConfigValues.includes(opt.value));
              const defaultValue = firstUnusedOption?.value || allConfigOptions[0]?.value || '';
              const selectedValue = selectedConfigs[widget.type] || defaultValue;
              const isSelectedOnDashboard = usedConfigValues.includes(selectedValue);

              return (
                  <div key={widget.type}>
                    <ListItem
                        disablePadding
                        sx={{
                          display: "flex",
                          padding: "10px 20px",
                          borderRadius: 2,
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                            cursor: "pointer",
                            borderRadius: 0,
                          },
                        }}
                    >
                      <ListItemText sx={{ paddingRight: "30px", flex: 1 }}>
                        <Typography level={"titleSmall"}>
                          {t(widget.defaultTitle)}
                        </Typography>
                        <Typography level={"titleSmallLight"}>
                          {t(widget.description)}
                        </Typography>
                      </ListItemText>

                      {hasConfigOptions && (
                        <Select
                          value={selectedValue}
                          onChange={(_, value) => handleConfigChange(widget.type, value)}
                          slotProps={{
                            listbox: {
                              sx: { zIndex: 9999 },
                            },
                          }}
                          sx={{
                            minWidth: 150,
                            marginRight: 2,
                            height: "42px",
                          }}
                        >
                          {allConfigOptions.map(option => (
                            <Option key={option.value} value={option.value}>
                              {t(option.label)}
                            </Option>
                          ))}
                        </Select>
                      )}

                      <Button
                          variant="contained"
                          onClick={() => {
                            if (widget.configurable) {
                              const configKey = widget.configKey ?? 'kpiType';
                              if (isSelectedOnDashboard) {
                                const instanceToRemove = instances.find(
                                  i => i.type === widget.type && (i.config?.[configKey] as string) === selectedValue
                                );
                                if (instanceToRemove) {
                                  onRemoveInstance(instanceToRemove.instanceId);
                                }
                              } else {
                                onSelect(widget.type, { [configKey]: selectedValue });
                              }
                            } else if (isAlreadyAdded) {
                              onSelect(widget.type);
                            } else {
                              onSelect(widget.type);
                            }
                          }}
                          sx={{
                            backgroundColor: theme.palette.primary[200],
                            height: "42px",
                            width: "42px",
                            minWidth: "42px",
                          }}
                      >
                        {(widget.configurable ? isSelectedOnDashboard : isAlreadyAdded) ? (
                            <RemoveRounded sx={{ color: "#802b00" }} />
                        ) : (
                            <AddRounded sx={{ color: "#802b00" }} />
                        )}
                      </Button>
                    </ListItem>
                    <Divider />
                  </div>
              );
            })}
          </List>
        </BasicModalContent>
      </Dialog>
  );
};

export default DashboardWidgetsModal;
